import { Component, Inject, Injector, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { ILocationService } from 'angular';
import { TemplateProjections } from 'app/shared/model/request-parameter.model';
import { templates } from 'handlebars';
import { LogbookUtils } from 'ng/src/app/core/logbook-utils';
import { OptionType } from 'ng/src/app/core/Option';
import { AppStateService } from 'ng/src/app/core/services/app-state.service';
import { Utils } from 'ng/src/app/core/utils';
import { CommitableValueAlias, DerivedValueAlias, RollbackValueAlias, ValueAlias } from 'ng/src/app/core/value';
import { Log } from 'ng/src/app/logs/log.model';
import { LogService } from 'ng/src/app/logs/log.service';
import { BaseComponent } from 'ng/src/app/shared/base.component';
import { CommandPaletteAction, CommandPaletteRegistration } from 'ng/src/app/shared/command-palette/command-palette.service';
import { BooleanInputDialogEntry, FormInputDialogForm, SelectorInputDialogEntry, StringInputDialogEntry, TemplateInputDialogEntry } from 'ng/src/app/shared/form-input-dialog/form-input-dialog.model';
import { FormInputDialogService } from 'ng/src/app/shared/form-input-dialog/form-input-dialog.service';
import { SelectorItem, SingleQuerySelectorItem } from 'ng/src/app/shared/item-selectors/item-selector.model';
import { ArrayGeneric, KeyOf } from 'ng/src/app/shared/model/app.model';
import { PersistentDialogService } from 'ng/src/app/shared/persistent-dialog/persistent-dialog.service';
import { AllUserSelectorModel } from 'ng/src/app/templates/all-user-selector/all-user-selector.component';
import { TemplateSegmentClipboardService } from 'ng/src/app/templates/clipboard/template-segment-clipboard.service';
import { TemplateService } from 'ng/src/app/templates/shared/template.service';
import { LogUser } from 'ng/src/app/users/shared/user.model';
import { ConfirmationService, FilterService, MenuItem } from 'primeng/api';
import { BehaviorSubject, combineLatest, Observable, ObservedValueOf, of, timer } from 'rxjs';
import { map, shareReplay, skip, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { NoteService } from '../../notes/shared/note.service';
import { Template, TemplateSegmentTypeId, TemplateTypeId, TemplateWithUsers } from '../shared/template.model';

@Component({
	selector: 'lb-template-list[log]',
	templateUrl: './template-list.component.html',
	styleUrls: ['./template-list.component.scss']
})
export class TemplateListComponent extends BaseComponent implements OnInit {
	@ViewChild('templateNewTemplatePicture') newTemplatePictureTemplate!: TemplateRef<any>;
	@ViewChild('templateCopyTemplate') templateCopyTemplate!: TemplateRef<any>;
	
	@Input()
	set log(val) {
		this._logSubject.next(val);
	}
	
	get log() {
		return this._logSubject.value;
	}
	
	private _logSubject = new BehaviorSubject<Log | null>(null) as BehaviorSubject<Log>;
	
	defaultTemplateItem!: SingleQuerySelectorItem<Template>;
	templates$!: Observable<(TemplateWithUsers & Readonly<{
		usageFilterOptions: Pick<Template, 'enterNotes' | 'evident'>,
		accessFilterOptions: Pick<TemplateWithUsers, 'allowAllUsers' | 'allowedUsers'>
	}>)[]>;

	usageFilter: Record<KeyOf<Template, 'enterNotes' | 'evident'>, ArrayGeneric<TemplateListComponent["usageFilterOptions"]>> = {
		enterNotes: 'No Filter',
		evident: 'No Filter'
	};
	
	accessFilter = Utils.defineGetterSetters({
		enabled: true,
		allowedUsers: [] as LogUser[],
	}, {
		filterUnused: {
			get: () => this.getFilterUnusedValue(),
			set: (val) => this.setFilterUnusedValue(val)
		},
	});
	
	readonly searchSubject = new BehaviorSubject<string>('');
	readonly searchValue = new ValueAlias(
		() => this.searchSubject.value,
		(val) => this.searchSubject.next(val.trimStart())
	);
	
	readonly usageFilterOptions: ['No Filter', 'Yes', 'No'] = ['No Filter', 'Yes', 'No'];
	readonly TEMPLATE_TYPES = TemplateTypeId;
	
	readonly templateMenuOptions = new WeakMap<Template, MenuItem[]>();
	copyingTemplate?: Template;
	
	get copyTemplateVisible() {
		return !!this.copyingTemplate;
	}
	
	set copyTemplateVisible(val) {
		if (!val)
			this.copyingTemplate = undefined;
	}

	private usageValues: Record<number, Record<KeyOf<Template, 'enterNotes' | 'evident'>, RollbackValueAlias<boolean>>> = {};
	private accessValues: Record<number, CommitableValueAlias<AllUserSelectorModel>> = {};
	
	get editingTemplate() {
		return this._editingTemplate;
	}
	
	set editingTemplate(val) {
		this._editingTemplate = val;
		this.clipboardService.contextTemplate = val;
		this.updateTemplateInSearch();
	}
	
	private _editingTemplate?: TemplateWithUsers;
	
	constructor(
		private templateService: TemplateService,
		private noteService: NoteService,
		private logService: LogService,
		private appStateService: AppStateService,
		private filterService: FilterService,
		private clipboardService: TemplateSegmentClipboardService,
		private formDialogService: FormInputDialogService,
		private dialogService: PersistentDialogService,
		private confirmService: ConfirmationService,
		@Inject('$location') private $location: ILocationService,
		injector: Injector
	) {
		super(injector);
	}
	
	ngOnInit(): void {
		this.templates$ = this._logSubject.pipe(
			takeUntil(this.onDestroy$),
			switchMap((log) => {
				return ((log.id === LogService.selectedLog?.id)
					? LogService.onTemplatesChanged$
					: this.templateService.fetch({log: log.id, sort: 'name,asc'}, TemplateProjections.WITH_USERS))
					.pipe(
						takeUntil(this.onDestroy$),
						map((rTemplates => {
							const templates = JSON.parse(JSON.stringify(rTemplates)) as TemplateWithUsers[];
							return templates.map((template) => {
								return Utils.defineGetters(template, {
									'usageFilterOptions': {
										get() {
											return {
												enterNotes: template.enterNotes,
												evident: template.evident
											};
										}
									},
									'accessFilterOptions': {
										get() {
											return {
												allowedUsers: template.allowedUsers,
												allowAllUsers: template.allowAllUsers
											}
										}
									}
								});
							});
						})),
						tap(() => this.accessValues = {})
					)
			}),
			switchMap((templates) => {
				return this.searchSubject.pipe(
					map((search) => {
						return templates.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));
					})
				);
			}),
			shareReplay({
				bufferSize: 1,
				refCount: true,
			})
		);
		
		this.templates$.pipe(
			takeUntil(this.onDestroy$),
			(source) => {
				let actionRegistration: CommandPaletteRegistration | undefined;
				return source.pipe(
					tap(templates => {
						actionRegistration?.unregister();
						const actions = templates
							.filter(t => !t.isDefaultTemplate)
							.map((t) => {
								return {
									tags: [
										'edit', 'template',
										t.templateType.id === TemplateTypeId.OPERATIONAL ? 'operational' : 'inspection',
										...OptionType.arrayOfSome(OptionType.of(t.hasSubTemplates).filter(t => t).map(t => 'subtemplate')),
									],
									group: 'Edit Template',
									icon: this.Icons.PENCIL,
									title: `Edit ${t.name}`,
									label: t.name,
									command: () => {
										this.editingTemplate = t;
									}
								} as CommandPaletteAction;
							});
						
						actionRegistration = this.registerActions(...[
							...actions,
							{
								tags: ['create', 'new', 'build'],
								group: 'Template Editor',
								label: 'Template',
								title: 'Create new template',
								icon: this.Icons.PLUS,
								command: () => {
									this.showNewTemplateDialog();
								}
							}
						]);
					})
				);
			}
		).subscribe();
		
		combineLatest([
			this.appStateService.locationChanged$
				.pipe(startWith(null)),
			this.templates$
		]).pipe(
			takeUntil(this.onDestroy$)
		).subscribe((results) => {
			this.editTemplateFromSearch(results[1]);
		});
		
		this.defaultTemplateItem = new SingleQuerySelectorItem(this.log.defaultTemplate);
		
		this.defaultTemplateItem.onChange$.pipe(
			takeUntil(this.onDestroy$),
			switchMap((newTemplate) => {
				return this.logService.setDefaultTemplate(this.log, newTemplate);
			})
		)
			.subscribe();

		this.filterService.register('usage-filter',
			(value: ArrayGeneric<ObservedValueOf<TemplateListComponent['templates$']>>['usageFilterOptions']) =>
			{
				
				if (this.usageFilter.enterNotes === 'No Filter' && this.usageFilter.evident === 'No Filter')
					return true;
				
				return compare(value.enterNotes, this.usageFilter.enterNotes) && compare(value.evident, this.usageFilter.evident);
				
				function compare(value: boolean, type: ArrayGeneric<TemplateListComponent['usageFilterOptions']>) {
					if (type === 'No Filter') return true;
					const tBool = type === 'Yes';
					return value === tBool;
				}
		});
		
		this.filterService.register('access-filter',
			(value: ArrayGeneric<ObservedValueOf<TemplateListComponent['templates$']>>['accessFilterOptions']) => {
				const options = this.accessFilter;
				
				if (options.filterUnused && !value.allowAllUsers && value.allowedUsers.length == 0) {
					return false;
				}
				else if (value.allowAllUsers || options.allowedUsers.length == 0) {
					return true;
				}
				
				const templateUsersMap = Utils.arrayToMap(value.allowedUsers, 'id');
				return value.allowedUsers.length > 0 && options.allowedUsers.every(usr => !!templateUsersMap[usr.id]);
			});
	}
	
	showNewTemplateDialog() {
		const nonEmptyStringValidator = (control: AbstractControl) => {
			const val = control.value;
			const trimmed = OptionType.nonNull(val)
				.castAsType(Utils.isStringPredicate)
				.filter(e => e.trim().length > 0)
				.getOrElse('');
			
			if (trimmed.length <= 0) {
				return { 'required': true };
			}
			
			return null;
		}
		
		const templateTypeControl = new FormControl<TemplateTypeId | null>(TemplateTypeId.INSPECTION, { validators: Validators.required });
		const subTemplateControl = new FormControl(false);
		const subTemplateName = new FormControl('', (e) => {
			if (subTemplateControl.value == true && templateTypeControl.value == TemplateTypeId.OPERATIONAL) {
				return nonEmptyStringValidator(e);
			}
			
			return null;
		});
		
		this.formDialogService.show(new FormInputDialogForm({
			name: new StringInputDialogEntry(new FormControl('', nonEmptyStringValidator))
				.setLabel('Template Name')
				.setPlaceholder('Required'),
			
			templateType: new SelectorInputDialogEntry(templateTypeControl, [
				SelectorItem.with(TemplateTypeId.INSPECTION, 'Inspection'),
				SelectorItem.with(TemplateTypeId.OPERATIONAL, 'Operational')
			])
				.setPlaceholder('Required')
				.setLabel('Template Type'),
			
			subTemplateEnabled: new BooleanInputDialogEntry(
				subTemplateControl,
			)
				.setLabel('Use Subtemplates')
				.setVisible(DerivedValueAlias.readOnly(
					ValueAlias.readOnly(templateTypeControl, 'value'),
					(e) => e === TemplateTypeId.OPERATIONAL
				)),
			
			subTemplateName: new StringInputDialogEntry(
				subTemplateName,
			)
				.setLabel('Subtemplate Name')
				.setPlaceholder('Required')
				.setVisible(DerivedValueAlias.readOnly(
					ValueAlias.readOnly(subTemplateControl, 'value'),
					(e) => e ?? false
				)),
			
			newPicture: new TemplateInputDialogEntry<any>(
				new FormControl(null),
				this.newTemplatePictureTemplate,
				Utils.defineGetters({}, {
					'templateType': {
						get: () => templateTypeControl.value,
						enumerable: true
					}
				}))
		})
			.setHeader('New Template')
			.setPosition('top')
			.setOnComplete(result => {
				this.templateService.postTemplate({
					name: result.name.value!.trim(),
					templateType: `/api/templateTypes/${result.templateType.value!}`,
					log: `/api/logs/${this.log.id}`
				})
					.pipe(
						switchMap(newTemplate => {
							if (newTemplate.templateType.id === TemplateTypeId.OPERATIONAL &&
								result.subTemplateEnabled.value && result.subTemplateName.value)
							{
								return this.templateService.postTemplateSegment({
									name: result.subTemplateName.value.trim(),
									template: newTemplate._links.self.href,
									type: `/api/templateSegmentTypes/${TemplateSegmentTypeId.SUB_TEMPLATE}`,
								}).pipe(
									map(() => newTemplate),
								);
							}
							return of(newTemplate);
						}),
						switchMap((res)=> {
							this.refreshTemplates();
							return this.templates$.pipe(
								takeUntil(this.onDestroy$),
								skip(1),
								take(1),
								map((update) => [res, update] as const)
							);
						}),
					)
					.subscribe(([res, templates]) => {
						const match = templates.find(t => t.id == res.id);
						if (match) {
							this.editingTemplate = match;
						}
					});
			})
		);
	}

	getUsageValue(template: Template, type: KeyOf<Template, 'enterNotes' | 'evident'>) {
		let value = this.usageValues[template.id]?.[type];

		if (value) return value;
		else if (!(template.id in this.usageValues)) {
			this.usageValues[template.id] = {
				enterNotes: RollbackValueAlias.of(template, 'enterNotes'),
				evident: RollbackValueAlias.of(template, 'evident')
			}
		}

		return this.usageValues[template.id][type];
	}
	
	getAccessValue(template: TemplateWithUsers): CommitableValueAlias<AllUserSelectorModel> {
		if (template.id in this.accessValues) {
			return this.accessValues[template.id];
		}
		
		return this.accessValues[template.id] = new CommitableValueAlias<AllUserSelectorModel>(
			() => ({
					allowAllUsers: template.allowAllUsers,
					allowedUsers: Array.of(...template.allowedUsers)
				}),
			(val) => {
				template.allowedUsers = val.allowedUsers;
				template.allowAllUsers = val.allowAllUsers;
			}
		)
	}
	
	getTemplateOptionsMenu(template: Template) {
		let items = this.templateMenuOptions.get(template);
		
		if (Array.isArray(items))
			return items;
		
		const deleteItem: MenuItem = {
			label: 'Delete',
			icon: this.Icons.TRASH,
			command: () => {
				this.deleteTemplate(template).subscribe();
			}
		};
		
		const copyItem: MenuItem = {
			label: 'Copy',
			icon: this.Icons.COPY,
			command: () => {
				this.copyingTemplate = template;
			}
		}
		
		this.templateMenuOptions.set(template, items = [copyItem, deleteItem]);
		return items;
	}
	
	editTemplateFromSearch(templates: TemplateWithUsers[]) {
		const editTemplateId = this.$location.search()['editTemplate'];
		if (editTemplateId == this.editingTemplate?.id) return;

		this.editingTemplate = templates.find(t => t.id == editTemplateId);
	}
	
	updateTemplateInSearch() {
		this.$location.search('editTemplate', this.editingTemplate?.id ?? null);
	}
	
	isDefaultTemplate(template: Template) {
		return this.defaultTemplateItem.getSingleValue()?.id === template.id;
	}

	saveUsageValue(template: Template, type: KeyOf<Template, 'enterNotes' | 'evident'>) {
		const value = this.getUsageValue(template, type);

		if (!value.hasChanges())
			return;

		this.templateService.updateTemplate(template, { [type]: value.value })
			.subscribe(
				() => {
					value.saveState();
					this.refreshTemplates();
				},
				() => value.rollback());
	}
	
	saveAccessValue(template: TemplateWithUsers) {
		const accessValue = this.getAccessValue(template);
		
		if (!accessValue.hasChanges()) return;
		
		const update = LogbookUtils.collectionUpdate(accessValue.value.allowedUsers, template.allowedUsers, 'allowedUsers');
		const toSend = {
			allowAllUsers: accessValue.value.allowAllUsers,
			...LogbookUtils.mapCollectionUpdateToIds(update)
		};
		
		return this.templateService.updateTemplate(template, toSend).subscribe(
			() => {
				accessValue.commit();
				this.refreshTemplates();
			},
			() => accessValue.discard()
		)
	}
	
	getFilterUnusedValue(): boolean {
		return OptionType.of(this.log)
			.nonNullable()
			.map(log => {
				return AppStateService.preferences.getValue(`${log.id}.template.filterUnused`, true)
			})
			.castAsType(Utils.isBooleanPredicate)
			.getOrElse(true);
	}
	
	setFilterUnusedValue(value: boolean) {
		if (this.log == null) {
			return;
		}
		
		AppStateService.preferences.setValue(`${this.log.id}.template.filterUnused`, value);
	}
	
	refreshTemplates() {
		this.log = this.log;
	}
	
	private deleteTemplate(template: Template) {
		return new Observable<boolean>((subscriber) => {
			this.confirmService.confirm({
				header: 'Delete Template',
				message: `Are you sure you want to delete ${template.name}?`,
				acceptLabel: 'Delete',
				rejectLabel: 'Cancel',
				acceptButtonStyleClass: 'p-button-danger',
				rejectButtonStyleClass: 'p-button-secondary',
				defaultFocus: 'reject',
				accept: () => subscriber.next(true),
				reject: () => subscriber.next(false)
			});
		})
			.pipe(
				takeUntil(this.onDestroy$),
				switchMap((proceed) => {
					if (!proceed) {
						return of(false);
					}
					
					// Wait for 100ms to allow the original dialog to close
					return timer(100).pipe(map(() => proceed));
				}),
				switchMap((proceed) => {
					if (!proceed)
						return of(null);
					
					return this.noteService.getLogNotes({
						template: template.id,
						size: 1,
						projection: 'idProjection'
					})
						.pipe(switchMap((r) => {
							const hasNotes = r.page.totalElements > 0;
							if (!hasNotes) {
								return of(true);
							}
							
							return new Observable<false | null>((subscriber) => {
								this.confirmService.confirm({
									header: 'Cannot Delete Template',
									message: `Template ${template.name} cannot be deleted because it is used by notes. Would you like to restrict access instead?`,
									acceptLabel: 'Yes',
									rejectLabel: 'Cancel',
									acceptButtonStyleClass: 'p-button-secondary',
									rejectButtonStyleClass: 'p-button-secondary',
									accept: () => subscriber.next(false),
									reject: () => subscriber.next(null)
								});
							});
						}));
				}),
				
				switchMap((canDelete) => {
					if (canDelete === null) {
						return of();
					}
					
					if (!canDelete) {
						const update: Partial<Template> & Record<string, any> = {
							allowAllUsers: false,
							ignoreBaseCollections: true,
							allowedUsers: []
						}
						
						return this.templateService.updateTemplate(template, update)
							.pipe(tap(() => this.refreshTemplates()));
					}
					
					return this.templateService.deleteTemplate(template)
						.pipe(
							tap(() => {
								this.refreshTemplates();
							})
						)
				})
			);
		}
	
	protected readonly templates = templates;
}